import { CTLineItem, SummaryDisplayItem } from '../types/cartTypes';

/**
 * Interface representing a group of items categorized by a common attribute (e.g. delegateRate).
 */
export interface ItemGroup {
  key: string;
  items: Array<CTLineItem | SummaryDisplayItem>;
}

/**
 * Groups line items by associationKey and attached add-ons to their parent items.
 * Support composite keys (associationKey + delegateRate) for precise grouping.
 */
export const groupLineItemsByAssociation = (
    lineItems: CTLineItem[], 
    productMetadata: Record<string, { price?: number; name?: Record<string, string> }> = {}, 
    currency = 'USD'
): SummaryDisplayItem[] => {
    if (!lineItems || !Array.isArray(lineItems)) {
        return [];
    }

    const groupMap: Record<string, CTLineItem[]> = {};
    const loose: CTLineItem[] = [];

    lineItems.forEach(item => {
        const assocKey = item.custom?.fields?.['associationKey'] as string | undefined;
        const delegateRate = item.custom?.fields?.['delegateRate'] as string | undefined;
        let groupKey: string | null = null;

        if (assocKey && delegateRate) {
            groupKey = assocKey + '||' + delegateRate;
        } else if (assocKey) {
            groupKey = assocKey;
        }

        if (groupKey) {
            if (!groupMap[groupKey]) groupMap[groupKey] = [];
            groupMap[groupKey].push(item);
        } else {
            loose.push(item);
        }
    });

    const transformedLineItems: SummaryDisplayItem[] = [];

    Object.values(groupMap).forEach(groupItems => {
        const passTypeId = import.meta.env.VITE_PASS_PRODUCT_TYPE_ID;

        let mainItem = groupItems.find(i =>
            ((i.custom?.fields?.['selectedOptionsList'] as string[]) || []).length > 0
        );

        if (!mainItem) {
            mainItem = groupItems.find(i => i.productType?.id === passTypeId);
        }

        if (!mainItem) {
            mainItem = groupItems[0];
        }

        const addOns = groupItems.filter(i => i !== mainItem);

        const addons = addOns.map(addon => {
            const name = addon.name && typeof addon.name === 'object'
                ? (addon.name['en'] || Object.values(addon.name)[0])
                : (addon.name as unknown as string);

            const price = addon.price?.value?.centAmount || addon.totalPrice?.centAmount || 0;
            return {
                name,
                total: { centAmount: price, currencyCode: currency },
                price: { centAmount: price, currencyCode: currency },
                custom: addon.custom
            };
        });

        let mainItemPriceCents = 0;
        if (mainItem.price?.value?.centAmount) {
            mainItemPriceCents = mainItem.price.value.centAmount * (mainItem.quantity || 1);
        } else if (mainItem.totalPrice?.centAmount) {
            mainItemPriceCents = mainItem.totalPrice.centAmount;
        } else if (productMetadata[mainItem.productId]?.price) {
            mainItemPriceCents = Math.round((productMetadata[mainItem.productId].price || 0) * 100 * (mainItem.quantity || 1));
        }

        transformedLineItems.push({
            ...mainItem,
            productName: mainItem.name && typeof mainItem.name === 'object'
                ? (mainItem.name['en'] || Object.values(mainItem.name)[0])
                : (mainItem.name as unknown as string),
            totalPrice: {
                centAmount: mainItemPriceCents,
                currencyCode: currency
            },
            price: {
                value: {
                    centAmount: mainItemPriceCents,
                    currencyCode: currency
                }
            },
            addons: addons.length > 0 ? addons : undefined
        });
    });

    loose.forEach(item => {
        const itemPriceCents = Math.round((item.price?.value?.centAmount || item.totalPrice?.centAmount || 0) * (item.quantity || 1));

        transformedLineItems.push({
            ...item,
            productName: item.name && typeof item.name === 'object'
                ? (item.name['en'] || Object.values(item.name)[0])
                : (item.name as unknown as string),
            totalPrice: {
                centAmount: itemPriceCents,
                currencyCode: currency
            },
            price: {
                value: {
                    centAmount: itemPriceCents,
                    currencyCode: currency
                }
            },
            addons: undefined
        });
    });

    return transformedLineItems;
};

/**
 * Alternative: Groups line items and uses productMetadata for add-on details
 */
export const groupLineItemsWithMetadata = (
    lineItems: CTLineItem[], 
    productMetadata: Record<string, { price: number; name: string }>, 
    currency = 'USD'
): SummaryDisplayItem[] => {
    if (!lineItems || !Array.isArray(lineItems)) {
        return [];
    }

    return (lineItems as unknown[]).map(p => {
        const li = p as CTLineItem;
        let addons: Array<{ name: string; total: { centAmount: number; currencyCode: string }; price: { centAmount: number; currencyCode: string } }> = [];

        const attachedAddOns = li.custom?.fields?.attachedAddOns;
        if (Array.isArray(attachedAddOns)) {
            addons = attachedAddOns.map((id: string) => {
                const details = productMetadata[id];
                if (!details) return null;

                const centAmount = Math.round(details.price * 100);
                return {
                    name: details.name,
                    total: {
                        centAmount,
                        currencyCode: currency
                    },
                    price: {
                        centAmount,
                        currencyCode: currency
                    }
                };
            }).filter((a): a is NonNullable<typeof a> => a !== null);
        }

        return {
            ...li,
            productName: li.name && typeof li.name === 'object' 
                ? (li.name['en'] || Object.values(li.name)[0])
                : (li.name as unknown as string),
            addons: addons.length > 0 ? addons : undefined
        } as SummaryDisplayItem;
    });
};

/**
 * Groups already transformed line items (with addons attached) by delegateRate.
 * Used for purely visual grouping in Order Summaries.
 */
export const groupItemsByDelegateRate = (items: Array<CTLineItem | SummaryDisplayItem>): ItemGroup[] => {
    if (!items || !Array.isArray(items)) return [];

    const groups: Record<string, Array<CTLineItem | SummaryDisplayItem>> = {};
    const otherItems: Array<CTLineItem | SummaryDisplayItem> = [];

    items.forEach(item => {
        const rate = (item.custom?.fields?.['delegateRate'] as string | undefined);

        if (rate) {
            if (!groups[rate]) {
                groups[rate] = [];
            }
            groups[rate].push(item);
        } else {
            otherItems.push(item);
        }
    });

    const result: ItemGroup[] = [];

    Object.keys(groups).forEach(rate => {
        result.push({
            key: rate,
            items: groups[rate]
        });
    });

    if (otherItems.length > 0) {
        result.push({
            key: 'Other',
            items: otherItems
        });
    }

    return result;
};
